/**
 * 首页商品列表组件
 * 融合PC端ProListSwiper和移动端RegularList2025的功能
 * 支持响应式设计，根据设备类型使用不同的布局和交互方式
 */

'use client';

import { ListProduct } from '@/api/types/public';
import { fetchProductList, fetchRankingProductList } from '@/api/product';
import HomePageTitle from '@/app/(home-page)/components/HomePageTitle';
import Image from '@/components/Image';
import { type JumpTarget, useJump } from '@/components/MagicJump';
import ProductCard from '@/components/Product/ProductCard';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import Link from 'next/link';
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
// @ts-ignore: Icon library missing type definitions
import { RightArrowOutlined, ToRightArrow } from '@zeelool/icon';
import './product-list-navigation.css';
import SkeletonProductCard from '@/components/Product/SkeletonProductCard';
import { useInView } from 'react-intersection-observer';

// 导入统一的类型定义
import type { CompleteTrackingParams, ProductListProps } from '@/app/(home-page)/types';
import { useProductListTabState } from '../../../../hooks/useProductListTabState';
import { getSDEventParams } from '@/app/(home-page)/utils/tracking';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { setBlockFlags } from '@/utils/feature-value';
import { setSession } from '@/utils/tools';
import { useRouter } from 'next/navigation';
import { handlePageFlagsInfo } from '../../../../hooks/useHomeSensorsTracking';
import useSWR, { unstable_serialize, useSWRConfig } from 'swr';
import rankBadge from './assets/rank-badge.svg';
import viewMoreArrow from './assets/view-more-arrow.svg';
import { buildHomeButtonTrackingParams } from '../../tracking';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useDataSpmEnabled, useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { useHomeSpmModuleCode } from '@/app/(home-page)/utils/spm-module';

interface RegularList2026ProductConfig extends JumpTarget {
  id: number;
  list_title: string;
  list_product_num: number;
  product_group_id: number;
  ranking_label?: 0 | 1 | 2;
  first_image_status?: number;
  jump_image?: string;
  image_title?: string;
  image_title_color?: string;
  position_jump_product_group?: {
    id?: number;
    name?: string;
    path?: string;
  };
}

type RegularList2026Product = ListProduct & {
  in_stock?: number | string;
  ranking_tag?: {
    type?: 1 | 2;
    metric?: string;
    value?: number;
    text?: string;
  };
};

type RegularList2026Content = Partial<ProductListProps['indexData']> &
  JumpTarget & {
    bg_color_status?: number;
    bg_color?: string;
    layout_subtitl_color?: string;
    product_card_style?: number;
    product_list?: RegularList2026ProductConfig[];
    view_more_status?: number;
  };

function isRegularList2026ProductArray(value: unknown): value is RegularList2026Product[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as { name?: unknown }).name === 'string',
    )
  );
}

function isRankingLabel(value: unknown): value is 0 | 1 | 2 {
  return value === 0 || value === 1 || value === 2;
}

function isAvailableProduct(product: RegularList2026Product) {
  const status = Number(product.status);
  const stock = Number(product.stock);

  if (Number.isFinite(status) && status !== 1) return false;
  if (Number.isFinite(stock) && stock <= 0) return false;

  return String(product.in_stock) !== '2';
}

function buildFirstOperationImageTrackingParams({
  config,
  indexData,
  moduleIndex,
  moduleTitle,
}: {
  config: RegularList2026ProductConfig;
  indexData: ProductListProps['indexData'];
  moduleIndex: number;
  moduleTitle?: string;
}): CompleteTrackingParams {
  const group = config.product_group || config.position_jump_product_group;

  return {
    module_index: moduleIndex,
    module_name: indexData.component_id,
    module_id: String(indexData.blocks_advances_id || indexData.id || ''),
    ...(moduleTitle ? { module_title: moduleTitle } : {}),
    module_tab_name: config.list_title,
    mkt_index: 0,
    mkt_index_sub: 0,
    mkt_jump_type: String(config.jump_type || ''),
    mkt_jump_content: String(config.Jump_target || ''),
    coupon_id_list: '',
    coupon_name_list: '',
    shop_sku: '',
    business_sku: '',
    product_sku: '',
    product_id: '',
    product_name: '',
    group_id: String(group?.id || config.product_group_id || ''),
    group_name: group?.name || '',
  };
}

function FirstOperationImageValidExposure({
  trackingKey,
  onExpose,
  children,
}: {
  trackingKey: string;
  onExpose: () => void;
  children: ReactNode;
}) {
  const { ref } = useValidExposure({ callback: onExpose, ts: trackingKey });

  return (
    <div ref={ref} className="h-full w-full">
      {children}
    </div>
  );
}

function ProductValidExposure({
  product,
  tabId,
  index,
  onExpose,
  children,
}: {
  product: ListProduct;
  tabId?: number;
  index: number;
  onExpose: (product: ListProduct, index: number) => void;
  children: ReactNode;
}) {
  const { ref } = useValidExposure({
    callback: () => onExpose(product, index),
    ts: `${tabId ?? ''}-${product.shop_sku || product.id}`,
  });

  return (
    <div ref={ref} className="h-full w-full">
      {children}
    </div>
  );
}

/** 循环内坑位打标辅助：与 useSpmDomAttr 同口径（开关关闭或编码为空返回 undefined，零 DOM 差异）。 */
const buildDomSpm = (enabled: boolean, code: string | undefined | null) =>
  enabled && code ? { 'data-spm': code } : undefined;

export default function ProductList({
  indexData,
  moduleIndex,
  isSale = false,
  isSaleSecond = false,
  onTrack,
  onExpTrack,
  onValidExpTrack,
  onBtnTrack,
  homeListData = [],
}: ProductListProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const router = useRouter();
  const toJumpTarget = useJump();
  const isPC = deviceType === EPlatform.WEB;
  const nestedContent = (
    indexData as ProductListProps['indexData'] & { content?: RegularList2026Content }
  ).content;
  // 兼容 204 接口已展开到模块顶层、但尚未经过 normalize 的数据。
  const content =
    nestedContent || (!isPC ? (indexData as unknown as RegularList2026Content) : undefined);
  const productLists = content?.product_list || [];
  const isRankingCardMode = !isPC && content?.product_card_style === 1;
  const isColorCardMode = !isPC && content?.product_card_style === 2;
  const isNewWapProductMode = isRankingCardMode || isColorCardMode;
  const isViewMoreEnabled = Number(content?.view_more_status) === 1;
  // 2026 WAP 楼层 SPM 模块编码（C 位）：榜单/色卡模式按楼块数据动态拼接，
  // 旧数据（非新版契约）保持原样不上报 spm_cnt。（hook 须无条件调用，模式判断放结果侧）
  const spmModuleCode = useHomeSpmModuleCode(indexData.component_id, indexData.id, 'home_wap');
  const spmModule = isRankingCardMode || isColorCardMode ? spmModuleCode : undefined;
  // 卡片内收藏（favorite_NNN）的模块编码：仅色卡模式楼层定义了 favorite 坑位；
  // 榜单模式无收藏按钮，旧数据无编码。
  const productCardSpmModule = isColorCardMode ? spmModule : undefined;
  // 商品坑位 D 位编码（动态商品位，序号 3 位：product_001）；模块码缺失（旧数据）时为 undefined，不硬造。
  const buildProductSpmCnt = useCallback(
    (index: number) =>
      spmModule ? `${spmModule}.product_${String(index + 1).padStart(3, '0')}` : undefined,
    [spmModule],
  );
  // View More 坑位 D 位编码；模块码缺失（旧数据）时为 undefined，不硬造。
  const viewMoreSpmCnt = spmModule ? `${spmModule}.view_more` : undefined;
  // 首位运营图的点击、有效曝光和 DOM 共用 open_campaign 坑位。
  const firstOperationImageSpmCnt = spmModule ? `${spmModule}.open_campaign` : undefined;
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：与上报编码同值，表外静默不打标。
  const moduleDomSpm = useSpmDomAttr(spmModule);
  const viewMoreDomSpm = useSpmDomAttr(viewMoreSpmCnt);
  const firstOperationImageDomSpm = useSpmDomAttr(firstOperationImageSpmCnt);
  // 循环内坑位（tab 等）打标开关：buildDomSpm 与 useSpmDomAttr 同口径。
  const dataSpmEnabled = useDataSpmEnabled();

  // 2026 WAP 只读取新版 content 契约；PC 分支继续保留复制时的旧字段行为。
  const displayData = useMemo(
    () =>
      isPC || !content
        ? indexData
        : {
            ...indexData,
            ...content,
            btn_display: isViewMoreEnabled ? 1 : 0,
            layout_subtitle_color: content.layout_subtitl_color,
          },
    [content, indexData, isPC, isViewMoreEnabled],
  );
  // 为每个组件实例生成唯一ID
  const uniqueId = useMemo(
    () => `product-list-${String(indexData.id || moduleIndex).replace(/[^a-zA-Z0-9-]/g, '-')}`,
    [indexData.id, moduleIndex],
  );

  // 状态管理 - 简化状态，只保留必要的状态，并使用 SWR 缓存
  const { clickTab, setClickTab } = useProductListTabState(uniqueId);
  const [emptyProductListIds, setEmptyProductListIds] = useState<Set<number>>(() => new Set());
  const visibleTabs = useMemo(
    () =>
      productLists
        .map((item, listIndex) => ({ item, listIndex, tab: String(listIndex + 1) }))
        .filter(({ item }) => !emptyProductListIds.has(item.id)),
    [emptyProductListIds, productLists],
  );
  const activeTab = visibleTabs.find(({ tab }) => tab === clickTab) || visibleTabs[0];
  const activeTabValue = activeTab?.tab;
  const activeProductListIndex = activeTab?.listIndex;
  const effectiveClickTab = activeTabValue || clickTab;
  const activeProductList = activeTab?.item;
  const tabScrollContainerRef = useRef<HTMLDivElement>(null);
  const tabButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const productGroupPath =
    displayData.product_group?.path ||
    activeProductList?.position_jump_product_group?.path ||
    displayData.product_group?.id ||
    activeProductList?.product_group_id;

  useEffect(() => {
    if (activeTabValue && clickTab !== activeTabValue) {
      setClickTab(activeTabValue);
    }
  }, [activeTabValue, clickTab, setClickTab]);

  useEffect(() => {
    const tabContainer = tabScrollContainerRef.current;
    const activeTabButton = tabButtonRefs.current[effectiveClickTab];

    if (!tabContainer || !activeTabButton) return;

    const containerBounds = tabContainer.getBoundingClientRect();
    const tabBounds = activeTabButton.getBoundingClientRect();
    const isPartiallyHidden =
      tabBounds.left < containerBounds.left || tabBounds.right > containerBounds.right;
    const scrollOffset =
      tabBounds.left + tabBounds.width / 2 - (containerBounds.left + containerBounds.width / 2);

    if (isPartiallyHidden && scrollOffset !== 0) {
      tabContainer.scrollBy({ left: scrollOffset, behavior: 'smooth' });
    }
  }, [effectiveClickTab]);

  // 添加已曝光商品ID的记录Set
  const exposedProductIds = useRef(new Set<string>()).current;
  const validExposedProductIds = useRef(new Set<string>()).current;
  const validExposedOperationImageKeys = useRef(new Set<string>()).current;
  const swiperRef = useRef<any>(null);

  // 计算按钮样式
  const btnStyle1 = useMemo(() => {
    if (displayData.btn_display === 2) {
      return {
        backgroundColor: displayData.tab_bg_color || 'transparent',
        color: displayData.tab_word_color || '#333',
        borderColor: displayData.tab_word_color || '#333',
      };
    }
    return {};
  }, [displayData]);

  const btnStyle2 = useMemo(() => {
    if (displayData.btn_display === 2) {
      return {
        backgroundColor: displayData.choosed_bg_color || '#333',
        color: displayData.choosed_word_color || '#fff',
      };
    }
    return {};
  }, [displayData]);

  const { ref: inViewRef, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  const staticProducts = displayData.products || [];
  const activeProductGroupId =
    activeProductList?.product_group_id || activeProductList?.position_jump_product_group?.id;
  const productGroupId =
    Number(activeProductGroupId) ||
    (isNewWapProductMode
      ? 0
      : Number(displayData.product_group?.id || displayData.product_group_id || 0));
  const productLimit = activeProductList?.list_product_num || 12;
  const rankingLabel = activeProductList?.ranking_label;
  const componentId = String(indexData.component_id || '');
  const productListKey = [
    isRankingCardMode
      ? 'regularList2026-ranking-products'
      : isColorCardMode
        ? 'regularList2026-color-products'
        : 'regularList2026-legacy-products',
    uniqueId,
    componentId,
    activeProductList?.id,
    activeProductListIndex,
    productGroupId,
    productLimit,
    rankingLabel,
    currency?.name,
  ] as const;
  const { cache } = useSWRConfig();
  // 后退时列表可能位于视口上方；已有缓存应立即恢复，避免重新挂载后退回骨架改变页面高度。
  const hasCachedProducts = cache.get(unstable_serialize(productListKey))?.data !== undefined;
  const shouldFetchProducts =
    (inView || hasCachedProducts) &&
    !staticProducts.length &&
    (isRankingCardMode ? productGroupId > 0 && isRankingLabel(rankingLabel) : productGroupId > 0);
  const {
    data: productListResponse,
    error: productListError,
    isLoading,
    mutate: refreshProducts,
  } = useSWR<RegularList2026Product[]>(
    shouldFetchProducts ? productListKey : null,
    async () => {
      if (isRankingCardMode && isRankingLabel(rankingLabel)) {
        const response = await fetchRankingProductList({
          group_id: productGroupId,
          ranking_tag: rankingLabel,
          page: 1,
          limit: productLimit,
          in_stock: 3,
          stock_sort_first: 1,
        });
        const responseProducts: unknown = response.data?.list;

        if (response.code !== 200 || !isRegularList2026ProductArray(responseProducts)) {
          throw new Error('regularList2026 榜单接口响应异常');
        }

        return responseProducts;
      }

      if (isColorCardMode) {
        const response = await fetchProductList({
          group_id: productGroupId,
          page: 1,
          limit: productLimit,
          currency: currency?.name,
          append_correlation: 1,
          in_stock: 3,
          stock_sort_first: 1,
        });
        const responseProducts: unknown = response.data?.list;

        if (response.code !== 200 || !isRegularList2026ProductArray(responseProducts)) {
          throw new Error('regularList2026 色卡接口响应异常');
        }

        return responseProducts;
      }

      const response = await fetchProductList({
        group_id: productGroupId,
        page: 1,
        limit: productLimit,
        currency: currency?.name,
        append_correlation: 1,
      });

      return Array.isArray(response?.data?.list)
        ? (response.data.list as RegularList2026Product[])
        : [];
    },
    {
      revalidateOnFocus: false,
    },
  );

  // 商品来源严格对应 content.product_list 中配置的商品分组和数量。
  const products = (
    staticProducts.length ? staticProducts : productListResponse || []
  ) as RegularList2026Product[];
  const hasAttemptedFetch =
    staticProducts.length > 0 || productListResponse !== undefined || !!productListError;

  const hasProducts = products && products.length > 0;
  const hasAvailableProducts = products.some((product) => isAvailableProduct(product));
  const activeProductListId = activeProductList?.id;
  const shouldHideActiveProductList =
    isNewWapProductMode &&
    activeProductListId !== undefined &&
    !isLoading &&
    !productListError &&
    Array.isArray(productListResponse) &&
    !hasAvailableProducts;

  useEffect(() => {
    if (!shouldHideActiveProductList || activeProductListId === undefined) return;

    setEmptyProductListIds((current) => {
      if (current.has(activeProductListId)) return current;

      const next = new Set(current);
      next.add(activeProductListId);
      return next;
    });
  }, [activeProductListId, shouldHideActiveProductList]);
  const showFirstOperationImage =
    isColorCardMode &&
    activeProductList?.first_image_status === 1 &&
    !!activeProductList.jump_image;
  const productTrackingIndexOffset = showFirstOperationImage ? 1 : 0;
  const firstOperationImageValidTrackingParams =
    showFirstOperationImage && activeProductList
      ? buildFirstOperationImageTrackingParams({
          config: activeProductList,
          indexData,
          moduleIndex,
          moduleTitle: displayData.layout_title,
        })
      : undefined;
  const firstOperationImageClickTrackingParams = firstOperationImageValidTrackingParams
    ? { ...firstOperationImageValidTrackingParams, click_content: '图片' }
    : undefined;

  // PC端Swiper配置 - 简化配置
  const pcSwiperOptions = useMemo(
    () => ({
      modules: [Navigation],
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      slidesPerView: 'auto' as const,
      navigation: {
        nextEl: `.${uniqueId}-next`,
        prevEl: `.${uniqueId}-prev`,
      },
    }),
    [uniqueId],
  );

  // 移动端Swiper配置 - 参考Vue RegularList2025
  const mobileSwiperOptions = useMemo(
    () => ({
      modules: [Navigation],
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      slidesPerView: isNewWapProductMode ? ('auto' as const) : 2.2,
      spaceBetween: isNewWapProductMode ? 6 : 5,
      slidesOffsetBefore: isNewWapProductMode ? 0 : 14,
      slidesOffsetAfter: isNewWapProductMode ? 0 : 14,
    }),
    [isNewWapProductMode],
  );

  const swiperOptions = isPC ? pcSwiperOptions : mobileSwiperOptions;

  const setPageFlagsInfo = function () {
    const sdParams = {
      module_id: String(displayData.blocks_advances_id), // 已审核
      module_title: displayData.layout_title,
      module_index: moduleIndex,
      module_name: displayData.component_id, // 已审核
      mkt_jump_type: '1',
      group_id: productGroupId,
    };
    const pageFlagsInfo = handlePageFlagsInfo(sdParams);
    if (pageFlagsInfo) {
      setSession('zl_home_page_flags_info', pageFlagsInfo);
    }
  };

  const trackHomeButton = (buttonName: string, spmCnt?: string) => {
    const params = buildHomeButtonTrackingParams({
      indexData,
      moduleIndex,
      moduleTitle: displayData.layout_title,
      buttonName,
    });

    onBtnTrack?.(spmCnt ? withSpmCnt(params, spmCnt) : params);
  };

  // Tab 切换处理
  // Tab 坑位 D 位编码；参考清单仅给热销楼层（榜单模式）定义了 tab_option 坑位，
  // 色卡模式楼层与模块码缺失（旧数据）时为 undefined，不硬造。
  const buildTabSpmCnt = useCallback(
    (tab: string) =>
      isRankingCardMode && spmModule
        ? `${spmModule}.tab_option_${tab.padStart(2, '0')}`
        : undefined,
    [isRankingCardMode, spmModule],
  );

  const handleTabClick = (tab: string, buttonName?: string) => {
    const tabSpmCnt = buildTabSpmCnt(tab);

    if (isNewWapProductMode && buttonName) trackHomeButton(buttonName, tabSpmCnt);

    if (isPC && tab === '3') {
      // 跳转到更多页面
      setPageFlagsInfo();
      setSession('zl_goodsListEnterPageScene', '首页坑位');
      router.push(`/goods-list/${productGroupPath}`);
    } else {
      setClickTab(tab);
      if (swiperRef.current?.swiper) {
        swiperRef.current.swiper.slideTo(0, 0);
      }
    }
  };

  // 埋点处理
  const handleSDHomeMktClick = useCallback(
    (item: ListProduct, index: number, clickIndex: number) => {
      try {
        if (onTrack) {
          const eventParams = getSDEventParams(
            item,
            displayData,
            moduleIndex,
            index + productTrackingIndexOffset,
            clickIndex,
            effectiveClickTab,
            homeListData,
            currency,
            userInfo,
            activeProductList?.list_title,
          );

          // 设置block_flags - 使用全局版本
          setBlockFlags({
            block_flags: eventParams.block_flags,
            block_flags_ext: eventParams.block_flags_ext,
            fine_ranking_type: eventParams.fine_ranking_type,
            recall_group_type: eventParams.recall_group_type,
            recommend_result_type: eventParams.recommend_result_type || '',
          });

          // spm_cnt 为商品坑位完整编码（D 位，按商品序号）；浅拷贝避免污染共享 eventParams。
          const productSpmCnt = buildProductSpmCnt(index);
          onTrack(productSpmCnt ? withSpmCnt(eventParams, productSpmCnt) : eventParams);
        }
      } catch (e) {
        logger.error('埋点错误:', e);
      }
    },
    [
      moduleIndex,
      onTrack,
      displayData,
      effectiveClickTab,
      homeListData,
      currency,
      userInfo,
      activeProductList?.list_title,
      buildProductSpmCnt,
      isNewWapProductMode,
      productTrackingIndexOffset,
    ],
  );

  // 通用的曝光处理逻辑
  const handleExposure = useCallback(
    (
      item: ListProduct,
      index: number,
      clickIndex: number,
      exposedSet: Set<string>,
      callback?: (eventParams: any) => void,
    ) => {
      try {
        // 检查商品是否已经曝光过
        const productKey = `${activeProductList?.id || effectiveClickTab}-${item.id}-${moduleIndex}`;
        if (exposedSet.has(productKey)) {
          return; // 如果已经曝光过，直接返回，不再触发曝光事件
        }

        if (callback) {
          const eventParams = getSDEventParams(
            item,
            displayData,
            moduleIndex,
            index + productTrackingIndexOffset,
            clickIndex,
            effectiveClickTab,
            homeListData,
            currency,
            userInfo,
            activeProductList?.list_title,
          );

          callback(eventParams);

          // 标记该商品已曝光
          exposedSet.add(productKey);
        }
      } catch (e) {
        logger.error('埋点错误:', e);
      }
    },
    [
      activeProductList?.id,
      activeProductList?.list_title,
      effectiveClickTab,
      moduleIndex,
      displayData,
      homeListData,
      currency,
      userInfo,
      productTrackingIndexOffset,
    ],
  );

  const handleExposeFunc = useCallback(
    (item: ListProduct, index: number, clickIndex: number) => {
      handleExposure(item, index, clickIndex, exposedProductIds, onExpTrack);
    },
    [handleExposure, exposedProductIds, onExpTrack],
  );

  const handleValidExposure = useCallback(
    (item: ListProduct, index: number, clickIndex: number) => {
      handleExposure(item, index, clickIndex, validExposedProductIds, (eventParams) => {
        if (!onValidExpTrack) return;

        // 商品有效曝光为 item 级，spm_cnt 追加坑位 D 位（动态商品位，序号 3 位：product_001）。
        const productSpmCnt = buildProductSpmCnt(index);
        onValidExpTrack(productSpmCnt ? withSpmCnt(eventParams, productSpmCnt) : eventParams);
      });
    },
    [handleExposure, validExposedProductIds, onValidExpTrack, buildProductSpmCnt],
  );

  // 检查是否显示标题和 View More。
  const shouldShowTitle =
    displayData?.layout_title_status === 1 && Boolean(displayData?.layout_title?.trim());
  const shouldShowViewMore = isViewMoreEnabled && Number(content.jump_type) !== 8;
  const showProductSkeleton = !hasProducts && (!hasAttemptedFetch || isLoading);

  // 新版只隐藏已经通过当前商品请求确认无可售商品的 Tab；旧版/PC 保留原判空行为。
  if (
    (isNewWapProductMode && (productLists.length === 0 || visibleTabs.length === 0)) ||
    (!isNewWapProductMode && hasAttemptedFetch && !hasProducts)
  ) {
    return null;
  }

  return (
    <div
      ref={inViewRef}
      className={`
        pro-picture w-full
        ${isSale ? 'is-sale' : ''}
        ${isSaleSecond ? 'is-sale-second' : ''}
        ${isNewWapProductMode ? 'my-0 pb-[12px]' : 'my-[40px] sm:my-[80px]'}
      `}
      style={
        !isPC && content?.bg_color_status === 1
          ? { backgroundColor: content.bg_color || '#FFFFFF' }
          : undefined
      }
      {...moduleDomSpm}
    >
      {isNewWapProductMode && (shouldShowTitle || shouldShowViewMore) && (
        <div
          className={`w-full px-[12px] pt-[20px] ${visibleTabs.length > 1 ? 'pb-[6px]' : 'pb-0'}`}
        >
          <div className="flex h-[24px] w-full items-center justify-between">
            {shouldShowTitle && (
              <h2
                className="min-w-0 flex-1 truncate whitespace-nowrap font-bb text-[20px] leading-[24px]"
                style={{ color: displayData.layout_title_color || '#282828' }}
              >
                {displayData.layout_title}
              </h2>
            )}
            {shouldShowViewMore && (
              <button
                type="button"
                className="ml-auto flex h-full shrink-0 cursor-pointer items-center gap-[4px] border-0 bg-transparent p-0 font-br text-[12px] font-normal leading-[14px] text-[#6c6c6c]"
                onClick={() => {
                  trackHomeButton(displayData.btn_text || 'View More', viewMoreSpmCnt);
                  setPageFlagsInfo();
                  setSession('zl_goodsListEnterPageScene', '首页坑位');
                  toJumpTarget(content);
                }}
                {...viewMoreDomSpm}
              >
                <span>{displayData.btn_text || 'View More'}</span>
                <Image
                  src={viewMoreArrow}
                  alt=""
                  width={16}
                  height={16}
                  aria-hidden
                  className="-rotate-90"
                />
              </button>
            )}
          </div>
          {shouldShowTitle && displayData.layout_subtitle && (
            <h4
              className="mt-[4px] w-[271px] max-w-full text-left font-br text-[12px] font-normal leading-[14px]"
              style={{ color: displayData.layout_subtitle_color || '#6c6c6c' }}
              dangerouslySetInnerHTML={{ __html: displayData.layout_subtitle }}
            />
          )}
        </div>
      )}

      {/* 标题和按钮区域 */}
      {!isNewWapProductMode && (
        <div
          className={`
          title-box flex items-center
          ${(() => {
            if (isPC) {
              return shouldShowTitle ? 'justify-between' : 'justify-end';
            }
            return shouldShowTitle ? 'justify-center' : 'justify-end';
          })()}
          px-[14px] sm:px-[24px] lg:px-[48px]
        `}
        >
          {/* 标题部分 - 只有在应该显示时才渲染 */}
          {shouldShowTitle && (
            <HomePageTitle
              indexData={displayData}
              variant={isPC ? 'inline' : 'default'}
              titleTag="h3"
              subtitleTag="h5"
              hasButton={displayData.btn_display > 0}
            />
          )}

          {/* PC端按钮区域 */}
          {isPC && displayData.btn_display === 2 && (
            <div className="tab-box flex">
              <button
                className="tab-btn flex items-center justify-center min-w-[113px] lg:min-w-[142px] h-[36px] lg:h-[50px] text-[12px] lg:text-[16px] border border-transparent rounded-[50px] px-[12px] lg:px-[15px] ml-[6px] lg:ml-[8px] cursor-pointer transition-all duration-200 hover:opacity-90"
                style={clickTab === '1' ? btnStyle2 : btnStyle1}
                onClick={() => handleTabClick('1')}
              >
                Eyeglasses
              </button>
              <button
                className="tab-btn flex items-center justify-center min-w-[113px] lg:min-w-[142px] h-[36px] lg:h-[50px] text-[12px] lg:text-[16px] border border-transparent rounded-[50px] px-[12px] lg:px-[15px] ml-[6px] lg:ml-[8px] cursor-pointer transition-all duration-200 hover:opacity-90"
                style={clickTab === '2' ? btnStyle2 : btnStyle1}
                onClick={() => handleTabClick('2')}
              >
                Sunglasses
              </button>
              <Link
                href={`/goods-list/${productGroupPath}`}
                className="tab-btn flex items-center justify-center min-w-[113px] lg:min-w-[142px] h-[36px] lg:h-[50px] text-[12px] lg:text-[16px] border border-transparent rounded-[50px] px-[12px] lg:px-[15px] ml-[6px] lg:ml-[8px] cursor-pointer transition-all duration-200 hover:opacity-90"
                style={btnStyle1}
                onClick={() => {
                  setSession('zl_goodsListEnterPageScene', '首页坑位');
                  handleTabClick('3');
                }}
              >
                <span>View More</span>
                <RightArrowOutlined className="ml-[5px] text-[12px]" />
              </Link>
            </div>
          )}

          {/* PC端单个按钮 */}
          {isPC && displayData.btn_display === 1 && (
            <Link
              href={`/goods-list/${productGroupPath}`}
              className="view-more min-w-[7.3958333333vw]  flex items-center justify-center px-[12px] lg:px-[15px] h-[36px] lg:h-[50px] text-[12px] lg:text-[16px] border border-transparent rounded-[50px] cursor-pointer transition-all duration-200 hover:opacity-90"
              style={{
                backgroundColor: displayData.btn_bg_color || '#333',
                color: displayData.btn_word_color || '#fff',
                borderColor: displayData.btn_word_color || '#333',
              }}
              onClick={() => {
                setPageFlagsInfo();
                setSession('zl_goodsListEnterPageScene', '首页坑位');
              }}
            >
              <span>{displayData.btn_text || 'View More'}</span>
              <RightArrowOutlined className="ml-[5px] text-[12px]" />
            </Link>
          )}
        </div>
      )}

      {/* 移动端Tab按钮 */}
      {isNewWapProductMode && visibleTabs.length > 1 && (
        <div
          ref={tabScrollContainerRef}
          className="scrollbar-hide flex items-end gap-[20px] overflow-x-auto px-[12px]"
        >
          {visibleTabs.map(({ item: productList, tab }) => (
            <button
              key={`${productList.id}-${tab}`}
              type="button"
              ref={(node) => {
                tabButtonRefs.current[tab] = node;
              }}
              className={`shrink-0 whitespace-nowrap border-x-0 border-t-0 bg-transparent px-0 py-[6px] font-br text-[14px] font-normal leading-[17px] ${
                effectiveClickTab === tab
                  ? 'border-b border-solid border-[#282828] text-[#282828]'
                  : 'border-b-0 text-[#6c6c6c]'
              }`}
              aria-pressed={effectiveClickTab === tab}
              onClick={() => handleTabClick(tab, productList.list_title)}
              {...buildDomSpm(dataSpmEnabled, buildTabSpmCnt(tab))}
            >
              {productList.list_title}
            </button>
          ))}
        </div>
      )}

      {/* 产品轮播区域 - 参考 SwiperNavigation 的容器类名 */}
      <div
        className={`
          product-swiper-container relative
          ${isNewWapProductMode ? 'mt-0' : 'mt-[20px] sm:mt-[32px]'}
        `}
      >
        {isNewWapProductMode && showProductSkeleton ? (
          <div
            className="regular-list-2026-skeleton-row flex gap-[6px] overflow-hidden px-[12px] pt-[12px]"
            aria-hidden
          >
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className="regular-list-2026-skeleton-slide w-[156px] shrink-0"
              >
                <div className="regular-list-2026-skeleton-card w-[156px] animate-pulse">
                  <div
                    className={`w-full rounded-[6px] bg-auxiliary-light-gray ${
                      isRankingCardMode ? 'aspect-[4/3]' : 'aspect-square'
                    }`}
                  />
                  <div className="mx-[6px] mt-[8px] h-[14px] rounded-[2px] bg-auxiliary-light-gray" />
                  <div className="mx-[6px] mt-[6px] h-[14px] w-[60%] rounded-[2px] bg-auxiliary-light-gray" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Swiper
            ref={swiperRef}
            {...swiperOptions}
            className={`
              sm:!px-[24px] lg:!px-[48px]
              ${isNewWapProductMode ? '!px-[12px] !pt-[12px]' : ''}
              ${isRankingCardMode ? '!pb-0' : '!pb-[2px]'}
            `}
          >
            {/* 色卡模式开启首位运营图片时，只在 2026 WAP 商品前插入运营板位。 */}
            {hasProducts && showFirstOperationImage && activeProductList && (
              <SwiperSlide
                key={`operation-${activeProductList.id}`}
                className="swiper-item relative !w-[156px]"
              >
                <FirstOperationImageValidExposure
                  trackingKey={`operation-${activeProductList.id}`}
                  onExpose={() => {
                    if (!firstOperationImageValidTrackingParams || !onValidExpTrack) return;

                    const trackingKey = `${moduleIndex}-${activeProductList.id}`;
                    if (validExposedOperationImageKeys.has(trackingKey)) return;

                    // 运营图有效曝光与点击共用完整坑位编码。
                    onValidExpTrack(
                      firstOperationImageSpmCnt
                        ? withSpmCnt(
                            firstOperationImageValidTrackingParams,
                            firstOperationImageSpmCnt,
                          )
                        : firstOperationImageValidTrackingParams,
                    );
                    validExposedOperationImageKeys.add(trackingKey);
                  }}
                >
                  <button
                    type="button"
                    className="relative block h-[231px] w-full overflow-hidden rounded-[6px] border-0 p-0"
                    aria-label={activeProductList.image_title || displayData.layout_title}
                    {...firstOperationImageDomSpm}
                    onClick={() => {
                      if (firstOperationImageClickTrackingParams) {
                        onTrack?.(
                          firstOperationImageSpmCnt
                            ? withSpmCnt(
                                firstOperationImageClickTrackingParams,
                                firstOperationImageSpmCnt,
                              )
                            : firstOperationImageClickTrackingParams,
                        );
                      }
                      toJumpTarget(activeProductList);
                    }}
                  >
                    <Image
                      src={activeProductList.jump_image!}
                      alt={activeProductList.image_title || displayData.layout_title || ''}
                      fill
                      sizes="156px"
                      className="object-cover"
                    />
                    {activeProductList.image_title && (
                      <span
                        className="absolute inset-x-0 bottom-0 flex items-center justify-center bg-gradient-to-t from-black/60 to-transparent px-[8px] py-[8px] text-center font-bb text-[14px] font-bold leading-[17px]"
                        style={{ color: activeProductList.image_title_color || '#FFFFFF' }}
                      >
                        {activeProductList.image_title}
                      </span>
                    )}
                  </button>
                </FirstOperationImageValidExposure>
              </SwiperSlide>
            )}

            {/* product_card_style: 1，榜单模式使用独立卡片结构。 */}
            {hasProducts &&
              isRankingCardMode &&
              products.map((item, index) => {
                const rankingType = item.ranking_tag?.type;
                const rankingText =
                  rankingLabel !== 0 &&
                  rankingType === rankingLabel &&
                  typeof item.ranking_tag?.text === 'string'
                    ? item.ranking_tag.text.trim()
                    : '';

                return (
                  <SwiperSlide
                    key={`${activeProductList?.id}-${item.id}`}
                    className={`swiper-item relative !w-[156px] ${
                      rankingText ? '!h-[207px]' : '!h-[179px]'
                    }`}
                  >
                    <ProductValidExposure
                      product={item}
                      tabId={activeProductList?.id}
                      index={index}
                      onExpose={(product, cardIndex) => {
                        handleValidExposure(product, cardIndex, 0);
                      }}
                    >
                      <ProductCard
                        product={item}
                        index={index}
                        spmModule={spmModule}
                        productSpmCnt={buildProductSpmCnt(index)}
                        className="regular-list-2026-ranking-card"
                        showWish={false}
                        showCorrelation={false}
                        showReviewStar
                        showTryOn={false}
                        showMember={false}
                        showTag={false}
                        showOriginalPrice
                        showName={false}
                        nameSuffix={displayData.name_suffix}
                        imageClassName="!aspect-[4/3] !rounded-[6px]"
                        productInfoClassName="!px-[6px] !pt-[8px]"
                        imageHoverRule={[]}
                        namePriceInline={false}
                        frameWidth="80%"
                        handelClick={(cardIndex, product) => {
                          handleSDHomeMktClick(product as ListProduct, cardIndex, 0);
                        }}
                      />
                      {rankingText && (
                        <span className="regular-list-2026-ranking-tag ml-[6px] mt-[8px] flex h-[20px] w-fit items-center rounded-[2px] bg-[#d9e2ff] px-[6px] py-[3px] font-br text-[12px] font-normal leading-[14px] text-[#44427a]">
                          {rankingText}
                        </span>
                      )}
                    </ProductValidExposure>
                    <span
                      className="absolute left-[8px] top-0 z-10 h-[20px] w-[16px]"
                      aria-label={`Rank ${index + 1}`}
                    >
                      <Image
                        src={rankBadge}
                        alt=""
                        width={16}
                        height={20}
                        aria-hidden
                        className="absolute inset-x-0 top-0 h-[19.3663px] w-[16px]"
                      />
                      <span className="relative z-10 flex justify-center pt-[3px] font-bb text-[11px] font-bold leading-[11px] text-white">
                        {index + 1}
                      </span>
                    </span>
                  </SwiperSlide>
                );
              })}

            {/* product_card_style: 2，色卡模式使用独立卡片结构。 */}
            {hasProducts &&
              isColorCardMode &&
              products.map((item, index) => (
                <SwiperSlide
                  key={`${activeProductList?.id}-${item.id}`}
                  className="swiper-item relative !w-[156px]"
                >
                  <ProductValidExposure
                    product={item}
                    tabId={activeProductList?.id}
                    index={index}
                    onExpose={(product, cardIndex) => {
                      handleValidExposure(product, cardIndex, 0);
                    }}
                  >
                    {/* 商品卡收藏坑位码由 productCardSpmModule 动态拼接，无需 SPM 作用域查表；主体坑位码
                        productSpmCnt 与点击/有效曝光事件 spm_cnt 同源同值（buildProductSpmCnt(index)）。 */}
                    <ProductCard
                      product={item}
                      index={index}
                      spmModule={productCardSpmModule}
                      productSpmCnt={buildProductSpmCnt(index)}
                      className="regular-list-2026-color-card"
                      wished={() => void refreshProducts()}
                      removed={() => void refreshProducts()}
                      showCorrelation
                      showReviewStar
                      showTryOn={false}
                      showMember={false}
                      showTag={false}
                      showOriginalPrice
                      showName={false}
                      nameSuffix={displayData.name_suffix}
                      imageClassName="!aspect-square !rounded-[6px]"
                      productInfoClassName="!px-[6px] !pt-[8px]"
                      imageHoverRule={[]}
                      namePriceInline={false}
                      frameWidth="80%"
                      handelClick={(cardIndex, product) => {
                        handleSDHomeMktClick(product as ListProduct, cardIndex, 0);
                      }}
                    />
                  </ProductValidExposure>
                </SwiperSlide>
              ))}

            {/* PC 和非新版契约场景继续保留复制时的商品卡行为。 */}
            {hasProducts &&
              !isNewWapProductMode &&
              products.map((item, index) => (
                <SwiperSlide
                  key={`${activeProductList?.id}-${item.id}`}
                  className={`swiper-item relative ${
                    isPC ? '!w-[23%] mr-[16px] last:!mr-0 !min-w-[328px] 2xl:!min-w-[auto]' : ''
                  }`}
                >
                  <ProductCard
                    product={item}
                    index={index}
                    showCorrelation
                    showReviewStar
                    showTryOn={false}
                    showMember={false}
                    showTag={false}
                    showOriginalPrice={false}
                    showName={false}
                    nameSuffix={displayData.name_suffix}
                    imageClassName="!aspect-[416/500] !rounded-[0px]"
                    imageHoverRule={[]}
                    namePriceInline={false}
                    frameWidth="80%"
                    handelClick={(cardIndex, product) => {
                      handleSDHomeMktClick(product as ListProduct, cardIndex, 0);
                    }}
                    onInView={(cardIndex, product) => {
                      handleExposeFunc(product as ListProduct, cardIndex, 0);
                    }}
                    onValidExposure={(cardIndex, product) => {
                      handleValidExposure(product as ListProduct, cardIndex, 0);
                    }}
                  />
                </SwiperSlide>
              ))}

            {!isNewWapProductMode &&
              showProductSkeleton &&
              Array.from({ length: isPC ? 4 : 2 }).map((_, index) => (
                <SwiperSlide
                  key={`skeleton-${index}`}
                  className={`
                swiper-item relative
                ${isPC ? '!w-[23%] mr-[16px] last:!mr-0 !min-w-[328px] 2xl:!min-w-[auto]' : ''}
              `}
                >
                  <div className="flex aspect-[150/324] w-[150px] items-center justify-center lg:aspect-[328/503] lg:w-[328px]">
                    <SkeletonProductCard />
                  </div>
                </SwiperSlide>
              ))}
          </Swiper>
        )}

        {/* PC端导航按钮 - 使用CSS文件中的样式类 */}
        {isPC && (
          <>
            <div
              className={`
                ${uniqueId}-next product-swiper-navigation absolute z-10 flex items-center justify-center
                w-[75px] lg:w-[100px] h-[53px] lg:h-[70px]
                top-[137px] lg:top-[185px] right-[48px] lg:right-[105px]
                rounded-[35px] border-2 border-white text-white cursor-pointer
              `}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
              }}
            >
              <ToRightArrow className="text-[30px] lg:text-[40px]" />
            </div>
            <div
              className={`
                ${uniqueId}-prev product-swiper-navigation absolute z-10 flex items-center justify-center
                w-[75px] lg:w-[100px] h-[53px] lg:h-[70px]
                top-[137px] lg:top-[185px] left-[48px] lg:left-[105px]
                rounded-[35px] border-2 border-white text-white cursor-pointer
              `}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
              }}
            >
              <ToRightArrow className="text-[30px] lg:text-[40px] rotate-180" />
            </div>
          </>
        )}
      </div>

      {/* 移动端View More按钮 */}
      {!isPC && !isNewWapProductMode && displayData.btn_display === 1 && (
        <div className="mt-[30px] flex justify-center">
          <Link
            href={`/goods-list/${productGroupPath}`}
            className="view-more min-w-[190px] h-[40px] leading-[40px] text-center rounded-[20px] text-[14px] font-bb relative transition-all duration-200 hover:opacity-90"
            style={{
              backgroundColor: displayData.btn_bg_color || '#333',
              color: displayData.btn_word_color || '#fff',
            }}
            onClick={() => {
              setPageFlagsInfo();
              setSession('zl_goodsListEnterPageScene', '首页坑位');
            }}
          >
            {displayData.btn_text || 'View More'}
          </Link>
        </div>
      )}
    </div>
  );
}
